import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { identity } from "lodash-es";
import { useCallback } from "react";
import {
  FieldPath,
  FieldValues,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import {
  FundFamilyUseAllocationTableOnRowAddFn,
  FundFamilyUseAllocationTableOnRowChange,
  FundFamilyUseAllocationTableOnRowRemove,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useArray, UseArraySetter } from "shared/hooks/useArrayState";
import { useFormStateDispatch } from "shared/utils/FormUtils";

export type PopulateEmptyFundFamilyUseAllocationTableRowFn = (
  row: FundFamilyUseAllocationTableRow,
) => FundFamilyUseAllocationTableRow;

export const useFundFamilyUseAllocationTableRowController = (
  rows: FundFamilyUseAllocationTableRow[],
  setRows: UseArraySetter<FundFamilyUseAllocationTableRow>,
  populateEmptyRow: PopulateEmptyFundFamilyUseAllocationTableRowFn = identity,
) => {
  const {
    updateIf: updateRowIf,
    add: addRow,
    removeAt: removeRowAt,
    removeIf: removeRowsIf,
  } = useArray(rows, setRows);

  const onRowAdd = useCallback<FundFamilyUseAllocationTableOnRowAddFn>(
    (row) => addRow(populateEmptyRow(row)),
    [addRow, populateEmptyRow],
  );

  const onRowChange = useCallback<FundFamilyUseAllocationTableOnRowChange>(
    ({ row }) => {
      updateRowIf((oldRow) => (oldRow.id === row.id ? row : null));
    },
    [updateRowIf],
  );

  const onRowRemove = useCallback<FundFamilyUseAllocationTableOnRowRemove>(
    (node) => removeRowsIf((row) => row.id === node.id),
    [removeRowsIf],
  );

  return { rows, onRowAdd, onRowChange, addRow, removeRowAt, onRowRemove };
};

export const useFundFamilyUseAllocationTableFormRowController = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  form: UseFormReturn<TFieldValues>,
  name: TFieldName,
  populateEmptyRow: PopulateEmptyFundFamilyUseAllocationTableRowFn = identity,
) => {
  const rows: FundFamilyUseAllocationTableRow[] = useWatch({
    control: form.control,
    name,
  });

  const setRows = useFormStateDispatch(
    form,
    name,
  ) as unknown as UseArraySetter<FundFamilyUseAllocationTableRow>;

  return useFundFamilyUseAllocationTableRowController(
    rows,
    setRows,
    populateEmptyRow,
  );
};
